import { postWithIdToken, IMSTokenParams } from "@utils/http/MSAuthAPI";
import { Country } from "@utils/constants/localisation";

export type TLineItem = {
    id: string;
    unit_amount: number;
    amount_ex_tax: number;
    tax: number;
    tax_description: string;
    description?: string;
};

export type TDiscountItem = {
    id: string;
    description: string;
    amount: number;
};

export interface IInvoicePreview {
    plan: TLineItem;
    addon: TLineItem[];
    discounts: TDiscountItem[];
    date_from: string;
    date_to: string;
    subtotal_ex_tax: number;
    total_tax: number;
    tax_description: string;
    total: number;
    amount_due: number;
    credits: number;
}

export type TCHBLivePrices = {
    plan: {
        id: string;
        total_exc_gst: string;
        total_inc_gst?: string;
    } | null;
    addons: Array<{
        id: string;
        total_exc_gst: string;
        total_inc_gst?: string;
    }>;
};

export interface IPreviewResponse {
    estimate: IInvoicePreview;
    live_prices: TCHBLivePrices;
}

export enum CouponLabel {
    dynamic = "dynamic",
    ignition = "ignition",
    ipb = "ipb",
}

type TCoupons = {
    [key in CouponLabel]?: string | number; // allow numbers if FE sends numeric values
};

export type TAddon = {
    chargebeeItemType: string,
    chargebeeItemKey: string,
    description?: string,
};
// ---------- Service ----------

interface IPreviewInvoiceService {
    getPreviewInvoice(
        country: Country,
        customerId: string,
        customerEmail: string,
        planId: string,
        subscriptionStartDate: string,
        coupons: TCoupons,
        addons: TAddon[] | undefined,
        IDTokenParams: IMSTokenParams
    ): Promise<IPreviewResponse>;
}

const PreviewInvoiceService: IPreviewInvoiceService = {
    async getPreviewInvoice(
        country: Country,
        customerId: string,
        customerEmail: string,
        planId: string,
        subscriptionStartDate: string,
        coupons: TCoupons,
        addons: TAddon[] | undefined,
        IDTokenParams: IMSTokenParams
    ): Promise<IPreviewResponse> {
        const payload: {
            country: string;
            customer_id: string;
            customer_email: string;
            plan_id: string;
            subscription_start_date: string;
            coupons?: TCoupons;
            addons?: TAddon[];
        } = {
            country,
            customer_id: customerId,
            customer_email: customerEmail,
            plan_id: planId,
            subscription_start_date: subscriptionStartDate,
            ...(coupons && Object.keys(coupons).length ? { coupons } : {}),
            ...(addons && addons.length ? { addons } : {}),
        };

        const resp = await postWithIdToken("v1/estimates", IDTokenParams, {}, payload);
        if (resp.status === 500) throw new Error("Server error");

        return resp.data;
    },
};

export default PreviewInvoiceService;
